import React, { useEffect, useRef, useState } from 'react';
import { FaRocket, FaArrowRight } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSection = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const { currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const typingRef = useRef(null);
  
  // Translations
  const translations = {
    ru: {
      openForProjects: "Мы открыты для новых проектов",
      creatingInnovative: "Создаем инновационные",
      teamDescription: "Мы — команда экспертов в цифровых технологиях, предлагающая полный спектр услуг от разработки до маркетинга для реализации вашего бизнес-потенциала.",
      startProject: "Начать проект",
      scrollDown: "Скролл вниз",
      technologies: "Технологии, с которыми мы работаем:",
      typingTexts: [
        'веб-приложения',
        'веб-сайты',
        'UI/UX дизайны',
        'облачные решения',
        'цифровые продукты'
      ],
      webDevelopment: "Разработка сайтов",
      frontendBackend: "Frontend & Backend",
      premium: "Premium",
      progress: "Прогресс",
      htmlCss: "HTML/CSS",
      layout: "Верстка",
      adaptiveDesign: "Адаптивный дизайн",
      backend: "Backend",
      databases: "Базы данных",
      sqlNoSql: "SQL, NoSQL",
      yearsExperience: "Лет опыта",
      projects: "Проектов",
      support: "Поддержка",
      security: "Безопасность",
      active: "Активный"
    },
    en: {
      openForProjects: "We're open for new projects",
      creatingInnovative: "Creating innovative",
      teamDescription: "We are a team of digital technology experts offering a full range of services from development to marketing to help realize your business potential.",
      startProject: "Start a project",
      scrollDown: "Scroll down",
      technologies: "Technologies we work with:",
      typingTexts: [
        'web applications',
        'websites',
        'UI/UX designs',
        'cloud solutions',
        'digital products'
      ],
      webDevelopment: "Website Development",
      frontendBackend: "Frontend & Backend",
      premium: "Premium",
      progress: "Progress",
      htmlCss: "HTML/CSS",
      layout: "Layout",
      adaptiveDesign: "Responsive Design",
      backend: "Backend",
      databases: "Databases",
      sqlNoSql: "SQL, NoSQL",
      yearsExperience: "Years Experience",
      projects: "Projects",
      support: "Support",
      security: "Security",
      active: "Active"
    }
  };
  
  // Get translated text
  const getText = (key) => translations[currentLanguage]?.[key] || translations.ru[key];
  
  // Проверка на мобильное устройство
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Проверяем при загрузке
    checkIfMobile();
    
    // Проверяем при изменении размера окна
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Частицы для анимированного фона
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      initParticles();
    };
    
    const initParticles = () => {
      particlesRef.current = [];
      
      for (let i = 0; i < 100; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speedX: Math.random() * 0.5 - 0.25,
          speedY: Math.random() * 0.5 - 0.25,
          color: currentTheme === 'light' 
            ? i % 3 === 0 ? '#1A56DB' : i % 3 === 1 ? '#7E22CE' : '#111827' 
            : i % 3 === 0 ? '#00F0FF' : i % 3 === 1 ? '#FF00F5' : '#FFFFFF'
        });
      }
    };
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Рисуем и обновляем частицы
      particlesRef.current.forEach(particle => {
        ctx.fillStyle = particle.color + (currentTheme === 'light' ? '20' : '40'); // Добавляем прозрачность
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Обновляем позицию
        particle.x += particle.speedX + (mouseRef.current.x - canvas.width / 2) * 0.0003;
        particle.y += particle.speedY + (mouseRef.current.y - canvas.height / 2) * 0.0003;
        
        // Проверяем границы
        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      });
      
      // Рисуем линии между близкими частицами
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const p1 = particlesRef.current[i];
          const p2 = particlesRef.current[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.strokeStyle = p1.color + Math.floor((1 - distance / 100) * (currentTheme === 'light' ? 100 : 255)).toString(16).padStart(2, '0');
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    
    handleResize();
    animate();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [currentTheme]);
  
  // Эффект печатающегося текста
  useEffect(() => {
    if (!typingRef.current) return;
    
    const texts = getText('typingTexts');
    
    let textIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;
    
    const type = () => {
      const current = textIndex % texts.length;
      const fullText = texts[current];
      
      if (typingRef.current) {
        if (isDeleting) {
          // Удаляем символы, но оставляем хотя бы один символ
          if (charIndex > 1) {
            typingRef.current.textContent = fullText.substring(0, charIndex - 1);
            charIndex--;
            typingSpeed = 50;
          } else {
            // Если остался последний символ, переходим к следующему слову
            isDeleting = false;
            textIndex++;
            // Заменяем последнюю букву на первую букву следующего слова
            const nextText = texts[textIndex % texts.length];
            typingRef.current.textContent = nextText.charAt(0);
            charIndex = 1;
          }
        } else {
          // Добавляем символы
          typingRef.current.textContent = fullText.substring(0, charIndex + 1);
          charIndex++;
          typingSpeed = 150;
        }
        
        // Если текст полностью напечатан
        if (!isDeleting && charIndex === fullText.length) {
          // Пауза перед удалением
          isDeleting = true;
          typingSpeed = 1000;
        }
      }
      
      rafRef.current = setTimeout(type, typingSpeed);
    };
    
    type();
    
    return () => {
      clearTimeout(rafRef.current);
    };
  }, [getText]);
  
  return (
    <div className={`relative h-screen flex items-center justify-center overflow-hidden ${
      currentTheme === 'light' ? 'bg-[#F5F7FA]' : 'bg-[#050505]'
    }`} id="home">
      {/* Анимированный фон с частицами */}
      <canvas
        ref={canvasRef}
        className={`absolute inset-0 z-0 ${
          currentTheme === 'light' ? 'bg-[#F5F7FA]' : 'bg-[#050505]'
        }`}
      />
      
      {/* Градиентная вуаль поверх фона */}
      <div className={`absolute inset-0 bg-gradient-to-b ${
        currentTheme === 'light' 
          ? 'from-[#F5F7FA] via-transparent to-[#F5F7FA]' 
          : 'from-[#050505] via-transparent to-[#050505]'
      } z-10`}></div>
      
      {/* Контент */}
      <div className="container mx-auto px-8 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-left" data-aos="fade-right">
            <div className="mb-6 inline-block">
              <div className={`flex items-center ${
                currentTheme === 'light' 
                  ? 'bg-gradient-to-r from-[#1A56DB]/10 to-[#7E22CE]/10' 
                  : 'bg-gradient-to-r from-[#00F0FF]/10 to-[#FF00F5]/10'
              } rounded-full px-4 py-2`}>
                <div className={`w-3 h-3 rounded-full ${
                  currentTheme === 'light' ? 'bg-[#1A56DB]' : 'bg-[#00F0FF]'
                } animate-pulse mr-2`}></div>
                <span className={`${
                  currentTheme === 'light' ? 'text-gray-700' : 'text-white'
                } text-sm font-medium`}>{getText('openForProjects')}</span>
              </div>
            </div>
            
            <h1 className={`text-4xl md:text-6xl font-bold mb-6 leading-tight ${
              currentTheme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              {getText('creatingInnovative')} <br />
              <span className={`text-transparent bg-clip-text bg-gradient-to-r ${
                currentTheme === 'light' 
                  ? 'from-light-primary to-light-secondary' 
                  : 'from-dark-primary to-dark-secondary'
              }`} ref={typingRef}>
                {getText('typingTexts')[0]}
              </span>
            </h1>
            
            <p className={`${
              currentTheme === 'light' ? 'text-gray-600' : 'text-gray-300'
            } text-lg mb-8 max-w-xl`}>
              {getText('teamDescription')}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className={`px-8 py-4 bg-gradient-to-r ${
                  currentTheme === 'light' 
                    ? 'from-light-primary to-light-secondary hover:shadow-lg hover:shadow-light-secondary/20' 
                    : 'from-dark-primary to-dark-secondary hover:shadow-lg hover:shadow-dark-secondary/20'
                } text-white font-bold rounded-full transition-all duration-300 flex items-center group`}>
                {getText('startProject')}
                <FaRocket className="ml-2 transform transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
            
            <div className="mt-12 hidden md:block">
              <p className={`${
                currentTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
              } mb-4`}>{getText('technologies')}</p>
              <div className="flex flex-wrap items-center gap-6">
                {/* React */}
                <div className={`flex items-center transition-opacity hover:opacity-100 ${
                  currentTheme === 'light' ? 'opacity-70' : 'opacity-60'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="38" height="38">
                    <path fill={currentTheme === 'light' ? "#1A56DB" : "#61DAFB"} d="M24,34.54C12.36,34.54,3,32.58,3,24S12.36,13.47,24,13.47,45,15.43,45,24,35.64,34.54,24,34.54Z" opacity="0.4"/>
                    <path fill={currentTheme === 'light' ? "#1A56DB" : "#61DAFB"} d="M24,38.19a2.67,2.67,0,1,1,2.66-2.66A2.65,2.65,0,0,1,24,38.19Zm10.68-28A17.1,17.1,0,0,1,24,15.54,17.05,17.05,0,0,1,13.33,10.2a2.93,2.93,0,0,0-2.41.34,2.89,2.89,0,0,0-1.3,2.11C9.32,15.06,11.9,19,17.36,23c-5.46,4-8,7.92-7.74,10.27a2.89,2.89,0,0,0,1.3,2.11,2.93,2.93,0,0,0,2.41.34A17.05,17.05,0,0,1,24,32.46a17.1,17.1,0,0,1,10.68,4.65,2.93,2.93,0,0,0,2.41.34,2.89,2.89,0,0,0,1.3-2.11c.27-2.35-2.28-6.31-7.74-10.27,5.46-4,8-7.91,7.74-10.27a2.89,2.89,0,0,0-1.3-2.11A2.93,2.93,0,0,0,34.68,10.2Zm1,22.71a.71.71,0,0,1-.35-.09A15,15,0,0,0,24,28,15,15,0,0,0,13.69,32.82a1,1,0,0,1-.8-.12,1,1,0,0,1-.42-.7c-.16-1.36,1.94-4.65,6.9-8.34A1,1,0,0,0,19.78,22a1,1,0,0,0-.41-1.66C14.41,16.65,12.31,13.36,12.47,12a1,1,0,0,1,.42-.7,1,1,0,0,1,.8-.12A15,15,0,0,0,24,16,15,15,0,0,0,34.31,11.18a1,1,0,0,1,.8.12,1,1,0,0,1,.42.7c.16,1.36-1.94,4.65-6.9,8.34A1,1,0,0,0,28.22,22a1,1,0,0,0,.41,1.66c5,3.69,7.06,7,6.9,8.34a1,1,0,0,1-.42.7A.93.93,0,0,1,34.68,32.91Z"/>
                  </svg>
                  <span className={`ml-2 text-sm font-medium ${
                    currentTheme === 'light' ? 'text-gray-800' : 'text-gray-300'
                  }`}>React</span>
                </div>
                
                {/* Next.js */}
                <div className={`flex items-center transition-opacity hover:opacity-100 ${
                  currentTheme === 'light' ? 'opacity-70' : 'opacity-60'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 180 180">
                    <mask height="180" width="180" x="0" y="0" maskUnits="userSpaceOnUse">
                      <circle cx="90" cy="90" r="90" fill="#fff"/>
                    </mask>
                    <g mask="url(#mask0)">
                      <circle cx="90" cy="90" r="90" fill={currentTheme === 'light' ? "#000" : "#fff"}/>
                      <path
                        d="M149.508 157.52L69.142 54H54v71.97h12.114V69.384l73.885 95.461a90.304 90.304 0 009.509-7.325z"
                        fill={currentTheme === 'light' ? "#fff" : "#000"}
                      />
                      <path
                        fill={currentTheme === 'light' ? "#fff" : "#000"}
                        d="M115 54H133V126H115z"
                      />
                    </g>
                  </svg>
                  <span className={`ml-2 text-sm font-medium ${
                    currentTheme === 'light' ? 'text-gray-800' : 'text-gray-300'
                  }`}>Next.js</span>
                </div>
                
                {/* Node.js */}
                <div className={`flex items-center transition-opacity hover:opacity-100 ${
                  currentTheme === 'light' ? 'opacity-70' : 'opacity-60'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 256 289">
                    <path 
                      fill={currentTheme === 'light' ? "#539E43" : "#8CC84B"} 
                      d="M128 288.464c-3.975 0-7.685-1.06-11.13-2.915l-35.247-20.936c-5.3-2.915-2.65-3.975-1.06-4.505c7.155-2.385 8.48-2.915 15.9-7.155c.796-.53 1.856-.265 2.65.265l27.032 16.166c1.06.53 2.385.53 3.18 0l105.739-61.217c1.06-.53 1.59-1.59 1.59-2.915V83.08c0-1.325-.53-2.385-1.59-2.915l-105.74-60.953c-1.06-.53-2.385-.53-3.18 0L20.405 80.166c-1.06.53-1.59 1.855-1.59 2.915v122.17c0 1.06.53 2.385 1.59 2.915l28.887 16.695c15.636 7.95 25.44-1.325 25.44-10.6V93.68c0-1.59 1.326-3.18 3.181-3.18h13.516c1.59 0 3.18 1.325 3.18 3.18v120.58c0 20.936-11.396 33.126-31.268 33.126c-6.095 0-10.865 0-24.38-6.625l-27.827-15.9C4.24 220.885 0 213.465 0 205.515V83.346C0 75.396 4.24 67.976 11.13 64L116.87 2.783c6.625-3.71 15.635-3.71 22.26 0L244.87 64C251.76 67.975 256 75.395 256 83.346v122.17c0 7.95-4.24 15.37-11.13 19.345L139.13 286.08c-3.445 1.59-7.42 2.385-11.13 2.385Zm32.596-84.009c-46.377 0-55.917-21.2-55.917-39.221c0-1.59 1.325-3.18 3.18-3.18h13.78c1.59 0 2.916 1.06 2.916 2.65c2.12 14.045 8.215 20.936 36.306 20.936c22.261 0 31.796-5.035 31.796-16.96c0-6.891-2.65-11.926-37.367-15.372c-28.886-2.915-46.907-9.275-46.907-32.33c0-21.467 18.02-34.187 48.232-34.187c33.921 0 50.617 11.66 52.737 37.101c0 .795-.265 1.59-.795 2.385c-.53.53-1.325 1.06-2.12 1.06h-13.78c-1.326 0-2.65-1.06-2.916-2.385c-3.18-14.575-11.395-19.345-33.126-19.345c-24.38 0-27.296 8.48-27.296 14.84c0 7.686 3.445 10.07 36.306 14.31c32.596 4.24 47.967 10.336 47.967 33.127c0 23.321-19.345 36.571-53.002 36.571Z"
                    />
                  </svg>
                  <span className={`ml-2 text-sm font-medium ${
                    currentTheme === 'light' ? 'text-gray-800' : 'text-gray-300'
                  }`}>Node.js</span>
                </div>
                
                {/* Tailwind CSS */}
                <div className={`flex items-center transition-opacity hover:opacity-100 ${
                  currentTheme === 'light' ? 'opacity-70' : 'opacity-60'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 32 32">
                    <path 
                      fill={currentTheme === 'light' ? "#38bdf8" : "#38bdf8"} 
                      d="M9,13.7q1.4-5.6,7-5.6c5.6,0,6.3,4.2,9.1,4.9q2.8.7,4.9-2.1-1.4,5.6-7,5.6c-5.6,0-6.3-4.2-9.1-4.9Q11.1,10.9,9,13.7ZM2,22.1q1.4-5.6,7-5.6c5.6,0,6.3,4.2,9.1,4.9q2.8.7,4.9-2.1-1.4,5.6-7,5.6c-5.6,0-6.3-4.2-9.1-4.9Q4.1,19.3,2,22.1Z"
                    />
                  </svg>
                  <span className={`ml-2 text-sm font-medium ${
                    currentTheme === 'light' ? 'text-gray-800' : 'text-gray-300'
                  }`}>Tailwind</span>
                </div>
                
                {/* Vue.js */}
                <div className={`flex items-center transition-opacity hover:opacity-100 ${
                  currentTheme === 'light' ? 'opacity-70' : 'opacity-60'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 256 221">
                    <path 
                      fill={currentTheme === 'light' ? "#41B883" : "#41B883"} 
                      d="M204.8 0H256L128 220.8L0 0h97.92L128 51.2L157.44 0h47.36Z"
                    />
                    <path 
                      fill={currentTheme === 'light' ? "#41B883" : "#41B883"} 
                      d="m0 0l128 220.8L256 0h-51.2L128 132.48L50.56 0H0Z"
                    />
                    <path 
                      fill={currentTheme === 'light' ? "#35495E" : "#35495E"} 
                      d="M50.56 0L128 133.12L204.8 0h-47.36L128 51.2L97.92 0H50.56Z"
                    />
                  </svg>
                  <span className={`ml-2 text-sm font-medium ${
                    currentTheme === 'light' ? 'text-gray-800' : 'text-gray-300'
                  }`}>Vue.js</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative mb-16 sm:mb-20 md:mb-0 hidden md:block" data-aos="fade-left">
            <div className="relative z-10 max-w-xl mx-auto">
              {/* Главная карточка */}
              <div className="relative overflow-hidden rounded-3xl">
                {/* Фоновый градиент */}
                <div className={`absolute inset-0 ${
                  currentTheme === 'light' 
                    ? 'bg-white' 
                    : 'bg-gradient-to-br from-[#0F172A] to-black'
                } z-0`}></div>
                
                {/* Сетка для декора */}
                <div className="absolute inset-0 z-0 opacity-20">
                  <div className={`h-full w-full ${
                    currentTheme === 'light' 
                      ? 'bg-grid-light opacity-10' 
                      : 'bg-grid-dark'
                  }`}></div>
                </div>
                
                {/* Цветные точки */}
                <div className={`absolute top-8 right-8 w-20 h-20 rounded-full ${
                  currentTheme === 'light' 
                    ? 'bg-light-primary blur-[80px] opacity-30' 
                    : 'bg-dark-primary blur-[80px] opacity-70'
                }`}></div>
                <div className={`absolute bottom-8 left-8 w-20 h-20 rounded-full ${
                  currentTheme === 'light' 
                    ? 'bg-light-secondary blur-[80px] opacity-30' 
                    : 'bg-dark-secondary blur-[80px] opacity-70'
                }`}></div>
                
                {/* Контент */}
                <div className="relative z-10 p-8">
                  {/* Верхняя часть с иконкой */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="relative flex h-12 w-12 items-center justify-center">
                        <div className={`absolute inset-0 rounded-xl ${
                          currentTheme === 'light' 
                            ? 'bg-gradient-to-r from-light-primary to-light-secondary opacity-20' 
                            : 'bg-gradient-to-r from-dark-primary to-dark-secondary opacity-20'
                        } blur`}></div>
                        <div className={`relative h-10 w-10 rounded-lg ${
                          currentTheme === 'light' 
                            ? 'bg-white' 
                            : 'bg-white'
                        } flex items-center justify-center`}>
                          <img src="/favicon.svg" alt="MoviBobby" className="w-full h-full" />
                        </div>
                      </div>
                      <h3 className={`text-lg font-semibold ${
                        currentTheme === 'light' 
                          ? 'text-gray-900' 
                          : 'text-white'
                      }`}>MoviBobby</h3>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <span className={`h-2 w-2 rounded-full ${
                        currentTheme === 'light' 
                          ? 'bg-green-500' 
                          : 'bg-[#00F0FF]'
                      } animate-pulse`}></span>
                      <span className={`text-xs ${
                        currentTheme === 'light' 
                          ? 'text-gray-500' 
                          : 'text-gray-400'
                      }`}>{getText('active')}</span>
                    </div>
                  </div>
                  
                  {/* Основной контент */}
                  <div className="mb-8">
                    <div className={`${
                      currentTheme === 'light' 
                        ? 'bg-gray-50 border border-gray-100' 
                        : 'bg-black/40 backdrop-blur-sm border border-white/5'
                    } rounded-xl p-5 mb-4`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className={`${
                            currentTheme === 'light' 
                              ? 'text-gray-900' 
                              : 'text-white'
                          } font-medium mb-1`}>{getText('webDevelopment')}</h4>
                          <p className={`text-xs ${
                            currentTheme === 'light' 
                              ? 'text-gray-500' 
                              : 'text-gray-400'
                          }`}>{getText('frontendBackend')}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          currentTheme === 'light' 
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600' 
                            : 'bg-gradient-to-r from-[#00F0FF] to-[#FF00F5]'
                        } text-white`}>{getText('premium')}</span>
                      </div>
                      
                      <div className={`h-[3px] w-full ${
                        currentTheme === 'light' 
                          ? 'bg-gray-200' 
                          : 'bg-gray-800'
                      } rounded-full mb-2`}>
                        <div className={`h-full w-[85%] rounded-full ${
                          currentTheme === 'light' 
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600' 
                            : 'bg-gradient-to-r from-[#00F0FF] to-[#FF00F5]'
                        }`}></div>
                      </div>
                      
                      <div className="flex justify-between text-xs">
                        <span className={`${
                          currentTheme === 'light' 
                            ? 'text-gray-500' 
                            : 'text-gray-400'
                        }`}>{getText('progress')}</span>
                        <span className={`${
                          currentTheme === 'light' 
                            ? 'text-gray-900' 
                            : 'text-white'
                        } font-medium`}>85%</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`${
                        currentTheme === 'light' 
                          ? 'bg-gray-50 border border-gray-100 hover:border-blue-500' 
                          : 'bg-black/40 backdrop-blur-sm border border-white/5 hover:border-[#00F0FF]'
                      } rounded-xl p-4 transition-all duration-300 group`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className={`h-8 w-8 rounded ${
                            currentTheme === 'light' 
                              ? 'bg-blue-100' 
                              : 'bg-[#00F0FF]/10'
                          } flex items-center justify-center`}>
                            <svg className={`h-4 w-4 ${
                              currentTheme === 'light' 
                                ? 'text-blue-500' 
                                : 'text-[#00F0FF]'
                            }`} viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <span className={`text-xs ${
                            currentTheme === 'light' 
                              ? 'text-blue-500 group-hover:text-gray-900' 
                              : 'text-[#00F0FF] group-hover:text-white'
                          } transition-colors`}>{getText('htmlCss')}</span>
                        </div>
                        <h5 className={`text-sm font-medium ${
                          currentTheme === 'light' 
                            ? 'text-gray-900' 
                            : 'text-white'
                        } mb-1`}>{getText('layout')}</h5>
                        <p className={`text-xs ${
                          currentTheme === 'light' 
                            ? 'text-gray-500' 
                            : 'text-gray-400'
                        }`}>{getText('adaptiveDesign')}</p>
                      </div>
                      
                      <div className={`${
                        currentTheme === 'light' 
                          ? 'bg-gray-50 border border-gray-100 hover:border-indigo-500' 
                          : 'bg-black/40 backdrop-blur-sm border border-white/5 hover:border-[#FF00F5]'
                      } rounded-xl p-4 transition-all duration-300 group`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className={`h-8 w-8 rounded ${
                            currentTheme === 'light' 
                              ? 'bg-indigo-100' 
                              : 'bg-[#FF00F5]/10'
                          } flex items-center justify-center`}>
                            <svg className={`h-4 w-4 ${
                              currentTheme === 'light' 
                                ? 'text-indigo-500' 
                                : 'text-[#FF00F5]'
                            }`} viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                            </svg>
                          </div>
                          <span className={`text-xs ${
                            currentTheme === 'light' 
                              ? 'text-indigo-500 group-hover:text-gray-900' 
                              : 'text-[#FF00F5] group-hover:text-white'
                          } transition-colors`}>{getText('backend')}</span>
                        </div>
                        <h5 className={`text-sm font-medium ${
                          currentTheme === 'light' 
                            ? 'text-gray-900' 
                            : 'text-white'
                        } mb-1`}>{getText('databases')}</h5>
                        <p className={`text-xs ${
                          currentTheme === 'light' 
                            ? 'text-gray-500' 
                            : 'text-gray-400'
                        }`}>{getText('sqlNoSql')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Статистика */}
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <div className={`text-2xl font-bold bg-clip-text text-transparent ${
                        currentTheme === 'light' 
                          ? 'bg-gradient-to-r from-blue-500 to-blue-500' 
                          : 'bg-gradient-to-r from-[#00F0FF] to-[#00F0FF]'
                      }`}>5+</div>
                      <p className={`text-xs ${
                        currentTheme === 'light' 
                          ? 'text-gray-500' 
                          : 'text-gray-400'
                      } mt-1`}>{getText('yearsExperience')}</p>
                    </div>
                    
                    <div className={`h-10 w-[1px] ${
                      currentTheme === 'light' 
                        ? 'bg-gray-200' 
                        : 'bg-gray-800'
                    }`}></div>
                    
                    <div className="text-center">
                      <div className={`text-2xl font-bold bg-clip-text text-transparent ${
                        currentTheme === 'light' 
                          ? 'bg-gradient-to-r from-indigo-500 to-indigo-500' 
                          : 'bg-gradient-to-r from-[#FF00F5] to-[#FF00F5]'
                      }`}>100+</div>
                      <p className={`text-xs ${
                        currentTheme === 'light' 
                          ? 'text-gray-500' 
                          : 'text-gray-400'
                      } mt-1`}>{getText('projects')}</p>
                    </div>
                    
                    <div className={`h-10 w-[1px] ${
                      currentTheme === 'light' 
                        ? 'bg-gray-200' 
                        : 'bg-gray-800'
                    }`}></div>
                    
                    <div className="text-center">
                      <div className={`text-2xl font-bold bg-clip-text text-transparent ${
                        currentTheme === 'light' 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600' 
                          : 'bg-gradient-to-r from-[#00F0FF] to-[#FF00F5]'
                      }`}>24/7</div>
                      <p className={`text-xs ${
                        currentTheme === 'light' 
                          ? 'text-gray-500' 
                          : 'text-gray-400'
                      } mt-1`}>{getText('support')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Декоративные элементы вокруг главной карточки */}
              <div className="absolute -top-4 -right-10 z-20">
                <div className={`${
                  currentTheme === 'light' 
                    ? 'bg-white border border-gray-100' 
                    : 'bg-black/80 backdrop-blur-sm border border-white/10'
                } p-3 rounded-lg shadow-xl`}>
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${
                      currentTheme === 'light' 
                        ? 'text-blue-500' 
                        : 'text-[#00F0FF]'
                    }`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
                    </svg>
                    <span className={`text-xs ${
                      currentTheme === 'light' 
                        ? 'text-gray-800' 
                        : 'text-white'
                    } font-medium`}>React / Next.js</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-10 z-20">
                <div className={`${
                  currentTheme === 'light' 
                    ? 'bg-white border border-gray-100' 
                    : 'bg-black/80 backdrop-blur-sm border border-white/10'
                } p-3 rounded-lg shadow-xl`}>
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${
                      currentTheme === 'light' 
                        ? 'text-indigo-500' 
                        : 'text-[#FF00F5]'
                    }`} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                    </svg>
                    <span className={`text-xs ${
                      currentTheme === 'light' 
                        ? 'text-gray-800' 
                        : 'text-white'
                    } font-medium`}>{getText('security')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Скролл индикатор */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center hidden md:flex">
        <div className="w-8 h-14 rounded-full border-2 border-[#333] flex justify-center">
          <div className="w-1 h-3 bg-[#00F0FF] rounded-full mt-2 animate-bounce"></div>
        </div>
        <p className="text-gray-400 text-sm mt-2">{getText('scrollDown')}</p>
      </div>
    </div>
  );
};

export default HeroSection; 